import { ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

/**
 * Page-like full-screen sheet for the mobile overlay stack. Deliberately NOT
 * a Radix Dialog: modal dialogs suppress the browser's swipe-back navigation
 * gesture (react-remove-scroll sets overscroll-behavior: contain on body),
 * and these sheets are navigation surfaces, not dialogs. The caller keeps the
 * node mounted while `open` is false long enough to play the exit animation
 * (MobileOverlayOutlet prunes at 350ms; the animation runs 300ms —
 * fill-mode-forwards holds the final frame in between).
 */
export default function FullScreenSheet({
  open,
  children,
}: {
  open: boolean;
  children: ReactNode;
}) {
  return createPortal(
    <>
      <div
        aria-hidden
        className={cn(
          "fixed inset-0 z-50 bg-black/60 duration-300",
          open
            ? "animate-in fade-in-0"
            : "animate-out fade-out-0 fill-mode-forwards"
        )}
      />
      <div
        role="dialog"
        aria-label="Details"
        className={cn(
          "fixed inset-0 z-50 flex flex-col bg-black text-white shadow-xl duration-300 ease-in-out",
          open
            ? "animate-in slide-in-from-right"
            : "animate-out slide-out-to-right fill-mode-forwards"
        )}
      >
        {children}
      </div>
    </>,
    document.body
  );
}
